import { useEffect } from 'react';
import { Route, useParams } from 'react-router-dom';
import { Tabs } from '@forgeglobal/design-framework';
import TabContent from '@tcs/common/components/atoms/TabContent';
import useRecentlyViewedAccounts, {
  recentItemFromAccount,
} from '@tcs/client-portal-common/hooks/useRecentlyViewedAccounts';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import AccountActivity from '@tcs/client-portal-common/components/templates/AccountActivity';
import AccountDetails from '@tcs/client-portal-individual/pages/Account/AccountDetails';
import DocumentDetails from '@tcs/client-portal-common/components/templates/DocumentDetails';
import AccountInfo from '@tcs/client-portal-common/components/templates/AccountInfo';
import AccountOverview from '@tcs/client-portal-common/components/templates/AccountOverview';
import { AccountsWithNameHeader } from '@tcs/client-portal-common/components/organisms/Header/AccountsWithNameHeader';
import useLinkedAccounts from '@tcs/client-portal-common/hooks/useLinkedAccounts';
import Spinner from '@tcs/common/components/atoms/Spinner';

const Account = () => {
  const {
    claims: { clientPortalIdentity },
  } = useSession();
  const { addRecentItem } = useRecentlyViewedAccounts(clientPortalIdentity);
  const { accountNumber } = useParams();
  const shouldLoadAccountData =
    !!accountNumber && accountNumber !== clientPortalIdentity;

  const { accounts, activeAccount, loading } = useLinkedAccounts(
    accountNumber,
    {
      enabled: shouldLoadAccountData,
    },
  );

  useEffect(() => {
    if (!activeAccount) return;
    addRecentItem(recentItemFromAccount(activeAccount), Persona.Individual);
  }, [activeAccount]);

  if (loading) {
    return <Spinner />;
  }

  if (!activeAccount) {
    return null;
  }

  return (
    <AccountsWithNameHeader
      accounts={accounts}
      loading={loading}
      pageTitle={activeAccount.fullName}
      tabs={() => (
        <Tabs>
          <Route
            path="*"
            element={
              <TabContent label="Overview" data-test-id="account-overview-tab">
                <AccountOverview accountNumber={accountNumber} />
              </TabContent>
            }
          />
          <Route
            path="/activity"
            element={
              <TabContent label="Activity" data-test-id="account-activity-tab">
                <AccountActivity accountNumber={accountNumber} />
              </TabContent>
            }
          />
          <Route
            path="/documents"
            element={
              <TabContent label="Documents" data-test-id="account-details-tab">
                <DocumentDetails
                  accountNumber={accountNumber}
                  onlyActiveAccounts={false}
                />
              </TabContent>
            }
          />
          <Route
            path="/account_holder_info"
            element={
              <TabContent
                label="Account Holder Info"
                data-test-id="account-info-tab">
                <AccountInfo />
              </TabContent>
            }
          />
          <Route
            path="/details"
            element={
              <TabContent label="Details" data-test-id="account-details-tab">
                <AccountDetails
                  accountNumber={accountNumber}
                  showBeneficiaries={true}
                  editEnabled={false}
                />
              </TabContent>
            }
          />
        </Tabs>
      )}
    />
  );
};

export default Account;
